import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cardPosition, richParts, sheetAtTop } from '../lib/tutorialCore'
import '../styles/tutorial.css'

// The first-login tour: a dark layer with one lit, real element on the screen and a step card beside it (a bottom sheet on the phone).
// Nothing on the page moves: the layer is fixed, on document.body, above the ? panel and the assistant. The step list comes from
// tourSteps(). Esc = Skip. An element that is not on the page (a card with nothing to show) just means a centred card, never a stop.
const SHEET_MAX = 700      // below this the step card is a bottom sheet
const WAIT_MS = 1200       // how long a step waits for its element (the page may still be loading)
const Rich = ({ text }) => richParts(text).map((p, i) => (p.bold ? <b key={i}>{p.text}</b> : <span key={i}>{p.text}</span>))
const sameRect = (a, b) => (!a && !b) || (!!a && !!b && a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height)

function visibleRect(sel) {
  const el = sel && document.querySelector(sel)
  if (!el) return { el: null, rect: null }
  const r = el.getBoundingClientRect()
  if (r.width < 2 || r.height < 2 || r.right <= 0 || r.left >= window.innerWidth) return { el, rect: null }
  const left = Math.max(4, r.left - 5), top = Math.max(4, r.top - 5)
  return { el, rect: { left, top, width: Math.min(window.innerWidth - 4, r.right + 5) - left, height: Math.min(window.innerHeight - 4, r.bottom + 5) - top } }
}

export default function TutorialTour({ steps, onFinish }) {
  const [i, setI] = useState(0)
  const [rect, setRect] = useState(null)
  const [view, setView] = useState({ w: window.innerWidth, h: window.innerHeight })
  const [cardSize, setCardSize] = useState({ w: 360, h: 220 })
  const card = useRef(null), next = useRef(null)
  const step = steps[Math.min(i, steps.length - 1)]
  const last = i >= steps.length - 1

  useEffect(() => { window.__hcTourOn = true; return () => { window.__hcTourOn = false; window.dispatchEvent(new CustomEvent('hc-tour-menu', { detail: { open: false } })) } }, [])
  useEffect(() => { const f = () => setView({ w: window.innerWidth, h: window.innerHeight }); window.addEventListener('resize', f); return () => window.removeEventListener('resize', f) }, [])

  // open what the step needs, then keep the light on the element (the drawer slides, a page finishes loading)
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('hc-tour-menu', { detail: { open: !!step.menu, page: step.navPage } }))
    if (step.fleetTab) window.dispatchEvent(new CustomEvent('hc-fleet-tab', { detail: { tab: step.fleetTab } }))   // Service package: the step shows a Fleet tab
    const t0 = Date.now()
    let scrolled = 0
    const tick = () => {
      const { el, rect: r } = visibleRect(step.target)
      if (el && scrolled < 6) { scrolled++; try { el.scrollIntoView({ block: 'nearest', inline: 'nearest' }) } catch (e) {} }
      if (r || !step.target || Date.now() - t0 > WAIT_MS) setRect(prev => (sameRect(prev, r) ? prev : r))
    }
    tick()
    const id = setInterval(tick, 90)
    return () => clearInterval(id)
  }, [step])

  useLayoutEffect(() => {
    if (!card.current) return
    const r = card.current.getBoundingClientRect()
    setCardSize(s => (s.w === r.width && s.h === r.height ? s : { w: r.width, h: r.height }))
  })
  useEffect(() => { if (next.current) next.current.focus({ preventScroll: true }) }, [i])

  const go = d => setI(n => Math.max(0, Math.min(steps.length - 1, n + d)))
  const finish = reason => onFinish(reason)
  // first in line for the keyboard while the tour is up: Esc closes the tour, not the panels under it; "/" and Cmd+K wait
  const keys = useRef()
  keys.current = e => {
    const shortcut = e.key === '/' || ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k')
    if (e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); finish('skip') }
    else if (e.key === 'ArrowRight') { e.preventDefault(); e.stopImmediatePropagation(); if (last) finish('done'); else go(1) }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); e.stopImmediatePropagation(); go(-1) }
    else if (shortcut) { e.preventDefault(); e.stopImmediatePropagation() }
    else if (e.key === 'Tab' && card.current) {
      const f = [...card.current.querySelectorAll('button')]
      if (!f.length) return
      const at = f.indexOf(document.activeElement)
      e.preventDefault()
      f[(at + (e.shiftKey ? -1 : 1) + f.length) % f.length].focus()
    }
  }
  useEffect(() => { const h = e => keys.current(e); window.addEventListener('keydown', h, true); return () => window.removeEventListener('keydown', h, true) }, [])

  const sheet = view.w < SHEET_MAX
  const top = sheet && sheetAtTop(rect, cardSize.h, view.h)
  const pos = sheet ? null : cardPosition(rect, cardSize, view)
  return createPortal(
    <div className="hc-tour" data-step={step.id}>
      <div className="hc-tour-block" />
      <div className={'hc-tour-hole' + (rect ? '' : ' none')} style={rect || { left: view.w / 2, top: view.h / 2, width: 0, height: 0 }} />
      <div ref={card} role="dialog" aria-modal="true" aria-labelledby="hc-tour-title" aria-describedby="hc-tour-text"
        className={'hc-tour-card' + (sheet ? ' sheet' + (top ? ' top' : '') : '')} style={pos ? { left: pos.left, top: pos.top } : undefined}>
        <div className="hc-tour-count">Step {i + 1} of {steps.length}</div>
        <h2 id="hc-tour-title">{step.title}</h2>
        <div id="hc-tour-text" className="hc-tour-text" aria-live="polite">
          {step.text && String(step.text).split('\n').map((l, n) => <p key={n}><Rich text={l} /></p>)}
          {step.bullets && <ul>{step.bullets.map((b, n) => <li key={n}><Rich text={b} /></li>)}</ul>}
        </div>
        <div className="hc-tour-foot">
          <div className="hc-tour-dots" aria-hidden="true">{steps.map((s, n) => <span key={s.id} className={n === i ? 'on' : n < i ? 'past' : ''} />)}</div>
          <button type="button" className="hc-tour-skip" onClick={() => finish('skip')}>Skip</button>
          {i > 0 && <button type="button" className="hc-tour-back" onClick={() => go(-1)}>Back</button>}
          <button ref={next} type="button" className="hc-tour-next" onClick={() => (last ? finish('done') : go(1))}>{last ? 'Finish' : 'Next'}</button>
        </div>
      </div>
    </div>, document.body)
}
